import React from 'react';

/**
 * The site's icons: simple line drawings (24x24 grid, 2px stroke) in
 * the text colour around them, so they follow the theme. Replaces
 * emoji everywhere - emoji render differently per device and clash
 * with the design.
 *
 *   <Icon name="pin" />            1em, inline with text
 *   <Icon name="sun" size={18} />  explicit pixel size
 */
const PATHS = {
    pin: <><path d="M12 21s-7-6.1-7-11.5A7 7 0 0 1 19 9.5C19 14.9 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>,
    shield: <path d="M12 3l7 3v5.5c0 4.4-3 8.2-7 9.5-4-1.3-7-5.1-7-9.5V6l7-3z" />,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3.5 6.5L12 13l8.5-6.5" /></>,
    users: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.6-3.6 3.3-5.5 6.5-5.5s5.9 1.9 6.5 5.5" /><path d="M15.5 4.8a3.5 3.5 0 0 1 0 6.4M17.5 14.8c2.2.6 3.6 2.4 4 5.2" /></>,
    sliders: <><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" /></>,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
    moon: <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />,
    eye: <><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
    'eye-off': <><path d="M3 3l18 18" /><path d="M10.6 5.1A9.7 9.7 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.1 3.9M6.6 6.6A17 17 0 0 0 2 12s3.6 7 10 7a9.6 9.6 0 0 0 5.4-1.6" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></>,
    check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
    x: <path d="M6 6l12 12M18 6L6 18" />,
    'check-circle': <><circle cx="12" cy="12" r="9" /><path d="M8 12.5l3 3 5-6" /></>,
    alert: <><path d="M12 3.5L2.5 20h19L12 3.5z" /><path d="M12 10v4.5M12 17.5h0" /></>,
    send: <><path d="M21 3L10.5 13.5" /><path d="M21 3l-6.5 18-4-7.5-7.5-4L21 3z" /></>,
    heart: <path d="M12 20s-7.5-4.6-9-9.3C1.9 7.3 4 4 7.3 4c2 0 3.6 1.1 4.7 2.8C13.1 5.1 14.7 4 16.7 4 20 4 22.1 7.3 21 10.7 19.5 15.4 12 20 12 20z" />,
    chat: <path d="M4 5h16v11H9l-5 4V5z" />,
    block: <><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6l12.8 12.8" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></>,
    more: <><circle cx="5" cy="12" r="1.2" /><circle cx="12" cy="12" r="1.2" /><circle cx="19" cy="12" r="1.2" /></>,
    back: <path d="M15 5l-7 7 7 7" />,
    trash: <><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></>,
    edit: <path d="M4 20h4L19 9l-4-4L4 16v4z" />,
    archive: <><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11h14V8M10 12h4" /></>,
    activity: <path d="M3 12h4l3-8 4 16 3-8h4" />,
    database: <><ellipse cx="12" cy="5.5" rx="8" ry="2.5" /><path d="M4 5.5v13c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-13M4 12c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5" /></>,
    server: <><rect x="3" y="4" width="18" height="7" rx="1.5" /><rect x="3" y="13" width="18" height="7" rx="1.5" /><path d="M7 7.5h0M7 16.5h0" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    disk: <><path d="M3 13l2.5-7.5A2 2 0 0 1 7.4 4h9.2a2 2 0 0 1 1.9 1.5L21 13v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5z" /><path d="M3 13h18M7 16.5h0M11 16.5h0" /></>,
    refresh: <><path d="M20 11a8 8 0 0 0-14.5-4.5L4 8" /><path d="M4 4v4h4M4 13a8 8 0 0 0 14.5 4.5L20 16" /><path d="M20 20v-4h-4" /></>,
    terminal: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9l3 3-3 3M12.5 15H17" /></>,
    store: <><path d="M4 9.5V20h16V9.5" /><path d="M3 4h18l-1.5 5.5a2.5 2.5 0 0 1-4.8 0 2.5 2.5 0 0 1-4.8 0 2.5 2.5 0 0 1-4.8 0L3 4z" /><path d="M10 20v-5h4v5" /></>,
    flag: <><path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" /></>,
    bug: <><rect x="7" y="7" width="10" height="13" rx="5" /><path d="M12 11v9M9 4l1.5 3M15 4l-1.5 3M3 13h4M17 13h4M4 8l3 2M20 8l-3 2M4 19l3-2M20 19l-3-2" /></>,
    layers: <><path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 13l9 5 9-5" /></>,
    swap: <><path d="M4 8h14l-3.5-3.5M20 16H6l3.5 3.5" /></>,
    play: <path d="M7 4.5v15l12-7.5-12-7.5z" />,
    'arrow-right': <path d="M5 12h14M13 6l6 6-6 6" />,
};

// Solid shapes read better than outlines at these sizes
const FILLED = new Set(['heart']);

function Icon({ name, size, className = '', title, ...rest }) {
    const shape = PATHS[name];
    if (!shape) return null;
    const dim = size ? `${size}px` : '1em';
    const filled = FILLED.has(name);
    return (
        <svg
            className={`icon icon-${name}${className ? ` ${className}` : ''}`}
            width={dim}
            height={dim}
            viewBox="0 0 24 24"
            fill={filled ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={filled ? 0 : 2}
            strokeLinecap="round"
            strokeLinejoin="round"
            role={title ? 'img' : undefined}
            aria-hidden={title ? undefined : true}
            aria-label={title}
            {...rest}
        >
            {title && <title>{title}</title>}
            {shape}
        </svg>
    );
}

export default Icon;
